import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { BellRinging, Plus, CheckCircle, Info, Warning, XCircle, Checks } from "@phosphor-icons/react";
import { toast } from "sonner";

const LEVEL = {
  info: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
  warning: { icon: Warning, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  critical: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
  success: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
};
const empty = { title: "", message: "", level: "warning", role: "all" };

export default function Alerts() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const invalidate = useInvalidate();
  const { data: alerts = [], isLoading } = useList("alerts", "/alerts", { refetchInterval: 8000 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const markRead = async (id) => { await api.put(`/alerts/${id}/read`); invalidate("alerts"); };
  const markAll = async () => { await api.put("/alerts/read-all"); invalidate("alerts"); toast.success("All marked read"); };
  const send = async () => {
    if (!form.title || !form.message) { toast.error("Title and message required"); return; }
    setSaving(true);
    try { await api.post("/alerts", { ...form, role: form.role === "all" ? null : form.role }); toast.success("Alert sent"); setOpen(false); setForm(empty); invalidate("alerts"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Alerts" subtitle="System notifications and custom alerts"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={markAll} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50" data-testid="mark-all-read"><Checks size={16} className="mr-1.5" />Mark all read</Button>
            {isAdmin && <Button data-testid="new-alert-btn" onClick={() => { setForm(empty); setOpen(true); }} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />New Alert</Button>}
          </div>
        } />

      {isLoading ? <div className="text-slate-500">Loading…</div> : alerts.length === 0 ? (
        <Panel><EmptyState icon={BellRinging} title="No alerts" hint="You're all caught up." /></Panel>
      ) : (
        <div className="space-y-2">
          {alerts.map((a) => {
            const L = LEVEL[a.level] || LEVEL.info;
            const Icon = L.icon;
            return (
              <Panel key={a.id} className={cn("p-4 flex items-start gap-3 transition-colors", !a.read && "border-l-2 border-l-blue-600")} data-testid={`alert-${a.id}`}>
                <div className={cn("p-2 rounded-md border shrink-0", L.bg, L.color)}><Icon size={18} weight="duotone" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{a.title}</p>
                    {!a.read && <span className="w-2 h-2 rounded-full bg-blue-600" />}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{a.message}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{new Date(a.created_at).toLocaleString()}</p>
                </div>
                {!a.read && <button onClick={() => markRead(a.id)} className="text-xs text-slate-500 hover:text-blue-600 shrink-0" data-testid={`read-${a.id}`}>Mark read</button>}
              </Panel>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader><DialogTitle className="font-heading">Create Custom Alert</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Title *"><Input data-testid="alert-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-slate-300" /></Field>
            <Field label="Message *"><Textarea data-testid="alert-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white border-slate-300" rows={3} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Level">
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="critical">Critical</SelectItem><SelectItem value="success">Success</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Send to">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="alert-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Everyone</SelectItem><SelectItem value="sales">Sales</SelectItem><SelectItem value="dispatcher">Dispatchers</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid="save-alert-btn" onClick={send} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Sending…" : "Send Alert"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
